import axios from 'axios';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IShipmentService } from '../../../../@posturinn/client-react';
import { resetToInitalValues } from '../../../../actions/registerShipmentActions';
import { downloadPdf } from '../../../../helpers/pdfHelpers/downloadApi';
import { ReactComponent as IconBox } from '../../../../icons/box.svg';
import IPageViewModel from '../../../../interfaces/umbracoNodes/pageViewModel';
import ISettingsViewModel from '../../../../interfaces/umbracoNodes/settingsViewModel';
import { IStore } from '../../../../reducers';
import { IRegisterShipmentReducer } from '../../../../reducers/registerShipmentReducer';
import Button from '../../../base/button';
import ReadOnlyValue from '../../../base/forms/readOnlyValue';
import { Column, Columns } from '../../../base/grid';
import { StyledRichText } from '../../../base/richTextStyleWrapper/richTextStyleWrapper';
import { P } from '../../../base/typography';
import { WizardPageProps } from '../../../wizard/wizard';
import StepFeedback from '../stepFeedback/stepFeedback';
import * as StyledStepReceipt from './stepReceipt.styles';

interface Props extends WizardPageProps {
	page: IPageViewModel;
}

const StepReceipt: FC<Props> = ({ page, setPage }) => {
	const { t } = useTranslation('registerShipment');
	const dispatch = useDispatch<any>();
	const [firstTimeLoading, setFirstTimeLoading] = useState(true);

	const registerShipmentReducer: IRegisterShipmentReducer = useSelector<IStore, IRegisterShipmentReducer>(
		store => store.registerShipmentReducer
	);
	const { settings }: { settings: ISettingsViewModel } = useSelector(({ pageReducer }) => pageReducer);
	const { injector } = useSelector(({ mobizReducer }) => mobizReducer);
	const shipmentService: IShipmentService = injector.get(IShipmentService);
	const { registrationKey, customerId, shipmentId } = registerShipmentReducer;
	const pdfUrl = shipmentService.fetchPdfForAnonymous(shipmentId, registrationKey, customerId);
	const info = {
		shipmentId,
		clientId: 'posturinn-web',
		domestic: registerShipmentReducer.stepInfoFormInfo.country.value === 'IS',
		loggedIn: false,
	};

	useEffect(() => {
		if (firstTimeLoading) {
			axios({
				url: pdfUrl,
				method: 'GET',
				responseType: 'blob',
			}).then(res => {
				downloadPdf(res.data, shipmentId);
				setFirstTimeLoading(false);
			});
		}
	}, [firstTimeLoading, setFirstTimeLoading, pdfUrl, shipmentId]);

	const resetRegisterShipment = () => {
		setPage(0);
		dispatch(resetToInitalValues());
	};

	useEffect(() => {
		return () => {
			dispatch(resetToInitalValues());
		};
	}, [dispatch]);

	return (
		<>
			<StyledStepReceipt.Wrapper>
				<Columns>
					<Column tablet={2 / 4}>
						{settings.Values.RegisterShipmentFeedback && <StepFeedback info={info} />}
						<StyledStepReceipt.ShipmentNumberWrapper>
							<StyledStepReceipt.ShipmentNumberLabel>
								{t('step-receipt.trackingnumber')}
							</StyledStepReceipt.ShipmentNumberLabel>
							<ReadOnlyValue value={shipmentId} />
						</StyledStepReceipt.ShipmentNumberWrapper>
						<StyledRichText content={page.Values.ReceiptText} />
					</Column>
				</Columns>
				<StyledStepReceipt.StyledFlex>
					<Button as="a" href={pdfUrl} target="_blank" marginRight="1rem">
						{t('step-receipt.print')}
					</Button>
					<Button onClick={resetRegisterShipment} darkText>
						<StyledStepReceipt.ButtonContentWrapper>
							<IconBox />
							<P marginBottom="0">{t('step-receipt.register-shipment')}</P>
						</StyledStepReceipt.ButtonContentWrapper>
					</Button>
				</StyledStepReceipt.StyledFlex>
			</StyledStepReceipt.Wrapper>
		</>
	);
};

export default StepReceipt;
